// SOVRANOVA — AI-not-human disclosure banner.
// Compliance requirement (see backend/services/compliance/jurisdictionPolicy.js
// and GET /api/compliance/policy): SOVRANOVA must disclose, plainly and
// up-front, that it is AI — not a human, not therapy — before the user leans
// on it. Fetches the disclosure copy once on load and shows a slim, dismissible
// bar. Dismissal is remembered per-device, keyed by a hash of the copy itself:
// if the copy ever changes (e.g. a new jurisdiction's wording), the hash
// changes and the banner re-shows even for users who dismissed the old text.
//
// Also carries a second, independent variant: a dependency-caution banner (SB
// 243 alignment). Dashboard.jsx is the sole caller of GET /api/welcome
// (already polled regularly post-login) and, on every successful response,
// publishes `window.dispatchEvent(new CustomEvent('sovranova:dependency-caution',
// { detail: { caution } }))` — this component just subscribes to that event
// rather than fetching /welcome itself, so the banner never duplicates the
// most expensive endpoint in the app (Claude Opus + ElevenLabs synthesis) and
// always reflects post-login state. `caution` mirrors `dependency_caution`
// from the response — DependencyMonitor's most recent assessment surfaced via
// backend/services/safety/dependencySurface.js. When true, this component
// shows a gentle "leaning on Sovranova a lot lately" note instead of the AI
// disclosure. It has its own dismissal key (a plain
// timestamp, not a copy hash — the caution copy is fixed) and re-shows itself
// after 7 days if the flag is still true, since overuse concerns are ongoing
// rather than a one-time disclosure. The AI disclosure takes priority while
// still pending (it's the compliance-mandated one); the caution only shows
// once the disclosure is dismissed or was never applicable.
//
// Mounted in main.jsx as a sibling of <CrisisLifeline/>, so it's always
// present — but it must never sit on top of (or crowd out) the lifeline pill,
// and it must never cover the session composer:
// - While visible it tags <html> with .ai-disclosure-visible and publishes its
//   measured height as the --ai-disclosure-h CSS var (kept fresh via
//   ResizeObserver); global.css uses both to pad .session-input-area up out
//   from under the bar. Removed again on dismiss/unmount. Both variants below
//   share this exact mechanism — only the text/dismiss-handler differ.
// - The AI-disclosure copy is clamped to 2 lines by default with a "More"
//   toggle that reveals the full required framing text — never permanently
//   truncated. The caution copy is short and shown in full, no clamp/toggle.
// - Wrapped in its own tiny error boundary that renders null on failure, so a
//   banner bug can never take down the root tree (which includes the
//   CrisisLifeline — that must survive anything this component does).
import React, { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { api } from '../services/api'

const DISMISS_KEY_PREFIX = 'sovranova_ai_disclosure_dismissed:'
const DEPENDENCY_DISMISS_KEY = 'sovranova_dependency_caution_dismissed_at'
const DEPENDENCY_CAUTION_TEXT =
  "You've been leaning on Sovranova a lot lately. That's okay — and real-world support matters too. Consider talking to someone you trust or a professional."
const DEPENDENCY_RESHOW_MS = 7 * 24 * 60 * 60 * 1000 // 7 days

// True if the caution hasn't been dismissed yet, or the dismissal is stale
// (>7 days old) so an ongoing concern re-surfaces rather than being silenced
// forever by a single dismissal.
function dependencyCautionDismissedRecently() {
  try {
    const raw = localStorage.getItem(DEPENDENCY_DISMISS_KEY)
    if (!raw) return false
    const dismissedAt = Number(raw)
    if (!Number.isFinite(dismissedAt)) return false
    return (Date.now() - dismissedAt) < DEPENDENCY_RESHOW_MS
  } catch { return false }
}

// Simple djb2 string hash -> unsigned 32-bit int as a string. Good enough to
// detect "the copy changed"; not a security control.
function hashCopy(str) {
  let hash = 5381
  for (let i = 0; i < str.length; i++) {
    hash = ((hash << 5) + hash) + str.charCodeAt(i)
    hash |= 0
  }
  return String(hash >>> 0)
}

// The visible bar. Only mounted while the banner should actually show, so its
// mount/unmount lifecycle drives the root class + height var cleanly.
function publishHeight(el) {
  if (!el) return
  document.documentElement.style.setProperty('--ai-disclosure-h', `${el.offsetHeight}px`)
}

function DisclosureBar({ text, onDismiss, variant = 'disclosure' }) {
  const wrapRef = useRef(null)
  const [expanded, setExpanded] = useState(false)
  const isCaution = variant === 'caution'

  // useLayoutEffect (not useEffect): the class-add + height publish must land
  // BEFORE the browser paints this mount, or the bar flashes on top of
  // .session-input-area/.session-complete-bar for one frame before their
  // clearance padding catches up on the next passive-effect tick.
  useLayoutEffect(() => {
    const el = wrapRef.current
    if (!el) return undefined
    const root = document.documentElement
    root.classList.add('ai-disclosure-visible')
    publishHeight(el)
    // ResizeObserver covers height changes we don't control (viewport
    // resize/rotation, font load); the layout effect below covers the one we
    // do (More/Less expand) deterministically, before paint.
    let ro
    if (typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(() => publishHeight(el))
      ro.observe(el)
    }
    return () => {
      if (ro) ro.disconnect()
      root.classList.remove('ai-disclosure-visible')
      root.style.removeProperty('--ai-disclosure-h')
    }
  }, [])

  // Re-publish synchronously whenever the expand toggle changes the bar's
  // height, so the composer padding never lags a frame behind.
  useLayoutEffect(() => {
    publishHeight(wrapRef.current)
  }, [expanded])

  return (
    <div ref={wrapRef} style={S.wrap} role="note" aria-label={isCaution ? 'Usage caution' : 'AI disclosure'}>
      <p style={!isCaution && !expanded ? { ...S.text, ...S.textClamped } : S.text}>{text}</p>
      {!isCaution && (
        <button
          type="button"
          onClick={() => setExpanded(e => !e)}
          style={S.more}
          aria-expanded={expanded}
          aria-label={expanded ? 'Collapse AI disclosure' : 'Show full AI disclosure'}
        >
          {expanded ? 'Less' : 'More'}
        </button>
      )}
      <button
        type="button"
        onClick={onDismiss}
        style={S.dismiss}
        aria-label={isCaution ? 'Dismiss usage caution' : 'Dismiss AI disclosure'}
      >
        ×
      </button>
    </div>
  )
}

function AiDisclosureInner() {
  const [copy, setCopy] = useState(null) // { text, hash } once loaded
  const [dismissed, setDismissed] = useState(true) // stay hidden until we know it should show
  const [dependencyCaution, setDependencyCaution] = useState(false)
  const [cautionDismissed, setCautionDismissed] = useState(true) // stay hidden until we know it should show

  useEffect(() => {
    let cancelled = false
    api.get('/compliance/policy')
      .then(res => {
        if (cancelled) return
        const disclosure = res && res.disclosure
        if (!disclosure || typeof disclosure !== 'string') return
        const hash = hashCopy(disclosure)
        let alreadyDismissed = false
        try { alreadyDismissed = localStorage.getItem(DISMISS_KEY_PREFIX + hash) === '1' } catch {}
        setCopy({ text: disclosure, hash })
        setDismissed(alreadyDismissed)
      })
      .catch(() => {
        // Best-effort disclosure — never blocks the app if the policy
        // endpoint is unreachable.
      })
    return () => { cancelled = true }
  }, [])

  // Dependency-caution flag (SB 243 alignment): DependencyMonitor's most
  // recent assessment. Sourced from Dashboard.jsx's own GET /api/welcome call
  // via a 'sovranova:dependency-caution' CustomEvent (see file header) rather
  // than fetching here — this component never calls /welcome itself.
  // Independent of the disclosure fetch above — best-effort, never blocks the
  // app (e.g. pre-login, or if Dashboard never mounts/fires the event).
  useEffect(() => {
    const onCaution = (e) => {
      const flag = !!(e && e.detail && e.detail.caution)
      setDependencyCaution(flag)
      setCautionDismissed(dependencyCautionDismissedRecently())
    }
    window.addEventListener('sovranova:dependency-caution', onCaution)
    return () => window.removeEventListener('sovranova:dependency-caution', onCaution)
  }, [])

  const showDisclosure = !!copy && !dismissed
  const showCaution = !showDisclosure && dependencyCaution && !cautionDismissed

  if (!showDisclosure && !showCaution) return null

  if (showDisclosure) {
    const onDismiss = () => {
      try { localStorage.setItem(DISMISS_KEY_PREFIX + copy.hash, '1') } catch {}
      setDismissed(true)
    }
    return <DisclosureBar text={copy.text} onDismiss={onDismiss} />
  }

  const onDismissCaution = () => {
    try { localStorage.setItem(DEPENDENCY_DISMISS_KEY, String(Date.now())) } catch {}
    setCautionDismissed(true)
  }
  return <DisclosureBar text={DEPENDENCY_CAUTION_TEXT} onDismiss={onDismissCaution} variant="caution" />
}

// Minimal error boundary: on any render error inside the banner, render
// nothing. The disclosure is best-effort UI; it must never crash the root
// tree that also hosts the CrisisLifeline.
class DisclosureErrorBoundary extends React.Component {
  constructor(props) {
    super(props)
    this.state = { failed: false }
  }

  static getDerivedStateFromError() {
    return { failed: true }
  }

  componentDidCatch(error) {
    console.error('SOVRANOVA AiDisclosure suppressed error:', error)
  }

  render() {
    return this.state.failed ? null : this.props.children
  }
}

export function AiDisclosure() {
  return (
    <DisclosureErrorBoundary>
      <AiDisclosureInner />
    </DisclosureErrorBoundary>
  )
}

const S = {
  wrap: {
    position: 'fixed',
    left: 0, right: 0, bottom: 0,
    // Below CrisisLifeline's 2147483000 so the lifeline pill always renders
    // on top of (never under) this bar.
    zIndex: 2147482000,
    display: 'flex', alignItems: 'center', gap: '0.5rem',
    // Right padding reserves room for the CrisisLifeline pill so the banner's
    // text/buttons never sit underneath it. Bottom padding adds the device's
    // safe-area inset (notched/gesture-bar phones) on top of the base 0.45rem
    // so the dismiss/more tap targets never sit under the home indicator.
    padding: '0.45rem clamp(120px, 20vw, 200px) calc(0.45rem + env(safe-area-inset-bottom, 0px)) 0.85rem',
    background: 'rgba(20,12,34,0.94)',
    borderTop: '1px solid rgba(201,162,39,0.35)',
    boxShadow: '0 -4px 16px rgba(0,0,0,0.25)',
    color: '#f3ede1',
    WebkitBackdropFilter: 'blur(6px)', backdropFilter: 'blur(6px)',
  },
  text: { margin: 0, flex: 1, fontSize: '0.74rem', lineHeight: 1.4 },
  textClamped: {
    display: '-webkit-box',
    WebkitBoxOrient: 'vertical',
    WebkitLineClamp: 2,
    overflow: 'hidden',
  },
  more: {
    flexShrink: 0, background: 'transparent', border: 'none',
    color: 'rgba(201,162,39,0.9)', fontSize: '0.72rem', fontWeight: 600,
    cursor: 'pointer', padding: '0.15rem 0.2rem', fontFamily: 'inherit',
    textDecoration: 'underline',
  },
  dismiss: {
    flexShrink: 0, background: 'transparent', border: 'none',
    color: 'rgba(243,237,225,0.6)', fontSize: '1.15rem', lineHeight: 1,
    cursor: 'pointer', padding: '0 0.25rem', fontFamily: 'inherit',
  },
}
